import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  Package,
  Globe,
  Drop,
  Leaf,
  Cow,
} from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { PRODUCTS, PHARMA_CROPS, CATTLE_BREEDS, IMAGES } from "../data";

const TABS = [
  { id: "dairy", label: "Dairy Products", icon: Drop, count: 8 },
  { id: "pharma", label: "Pharma Crops", icon: Leaf, count: 17 },
  { id: "cattle", label: "Cattle", icon: Cow, count: 3 },
];

const PACKAGING = [
  { spec: "200g · 500g · 1kg retail-ready", desc: "Private label & branded retail packs" },
  { spec: "5kg · 15kg HORECA tubs", desc: "Hospitality and food-service kitchens" },
  { spec: "25kg export-grade bags", desc: "Bulk industrial & manufacturing supply" },
];

export default function Products() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dairy");

  // Allow ?tab=pharma deep links from the pillars section
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t && TABS.find((x) => x.id === t)) setActiveTab(t);
  }, [location.search]);

  return (
    <div data-testid="products-page">
      <PageHero
        overline="Product Catalog"
        title={
          <>
            Three pillars.
            <br />
            <span className="italic text-terracotta">One catalog.</span>
          </>
        }
        subtitle="Dairy, pharma-grade crops, and premium cattle — every SKU is export-ready, batch-traceable, and built around institutional consistency."
        image={IMAGES.cheese}
        testId="products-hero"
      />

      {/* Tab Bar */}
      <div
        className="sticky top-20 z-30 bg-bone/95 backdrop-blur border-b border-olive/10"
        data-testid="products-tab-bar"
      >
        <div className="max-w-8xl mx-auto px-6 lg:px-12 flex flex-wrap items-center gap-2 lg:gap-1 py-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`products-tab-${tab.id}`}
                className={`flex items-center gap-3 px-5 lg:px-7 py-3 lg:py-4 text-xs lg:text-sm font-medium uppercase tracking-widest2 transition-colors ${
                  active
                    ? "bg-olive-deep text-bone"
                    : "text-olive-soft hover:text-olive-deep hover:bg-sand"
                }`}
              >
                <Icon size={18} weight="duotone" />
                <span>{tab.label}</span>
                <span
                  className={`font-mono text-[0.62rem] ${
                    active ? "text-terracotta" : "text-olive-soft/70"
                  }`}
                >
                  {String(tab.count).padStart(2, "0")}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Catalog */}
      {activeTab === "dairy" && <DairyCatalog />}
      {activeTab === "pharma" && <PharmaCatalog />}
      {activeTab === "cattle" && <CattleCatalog />}

      {/* Quality Assurance — shared across pillars */}
      <section className="bg-olive-deep text-bone py-24 lg:py-32" data-testid="products-quality">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline text-terracotta">Quality Assurance</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-6 leading-tight text-bone">
              Every batch. Traceable to a farm lot.
            </h2>
            <p className="mt-8 text-bone/70 leading-relaxed">
              Each carton, tin, bag, and animal carries a lot or lineage code
              that resolves back to the soil block, herd, feed batch, and
              processing line. For regulated buyers, exporters and pharma
              clients, that is documentation you can put in front of an
              auditor.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-px bg-bone/15">
            {[
              { Icon: ShieldCheck, title: "Lot-level QC", body: "Each batch sampled and held against export-grade parameters." },
              { Icon: Package, title: "Custom packaging", body: "Retail, HORECA, bulk industrial and pharma drum formats." },
              { Icon: Globe, title: "Documentation pack", body: "FSSAI, COA, MOA, phyto-sanitary, halal — issued per shipment." },
            ].map((b, i) => (
              <div
                key={i}
                className="bg-olive-deep p-8 min-h-[260px] flex flex-col justify-between"
                data-testid={`quality-block-${i}`}
              >
                <b.Icon size={32} weight="duotone" className="text-terracotta" />
                <div>
                  <h3 className="font-heading text-2xl text-bone">{b.title}</h3>
                  <p className="mt-3 text-sm text-bone/70 leading-relaxed">
                    {b.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging — only meaningful for dairy and pharma */}
      {activeTab !== "cattle" && (
        <section className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="products-packaging">
          <span className="overline">Packaging Options</span>
          <h2 className="h-section text-4xl md:text-5xl mt-6 max-w-3xl">
            From 200g retail to 25kg export bags.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
            {PACKAGING.map((p, i) => (
              <div
                key={i}
                className="card-flat p-8 min-h-[180px]"
                data-testid={`packaging-option-${i}`}
              >
                <span className="font-mono text-xs tracking-widest2 text-terracotta">
                  FORMAT {i + 1}
                </span>
                <h3 className="font-heading text-2xl mt-4 text-olive-deep">
                  {p.spec}
                </h3>
                <p className="mt-3 text-sm text-olive-soft">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function CatalogGrid({ items, badge, testidPrefix, useLabel = "Intended Use" }) {
  return (
    <section
      className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
      data-testid={`${testidPrefix}-catalog`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <article
            key={p.name}
            className="card-flat group overflow-hidden"
            data-testid={`${testidPrefix}-${p.name.toLowerCase().replace(/[\s/]+/g, "-")}`}
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-sand">
              <img
                src={p.img}
                alt={p.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {p.flagship && (
                <span className="absolute top-4 left-4 badge-pill bg-terracotta text-[#1A1A1A] border-terracotta">
                  Flagship
                </span>
              )}
              {p.coming && (
                <span className="absolute top-4 left-4 badge-pill bg-bone/95">
                  Coming Soon
                </span>
              )}
              {!p.coming && (
                <span className="absolute top-4 right-4 badge-pill bg-bone/90">
                  Export Ready
                </span>
              )}
            </div>
            <div className="p-7">
              <h3 className="font-heading text-3xl text-olive-deep">
                {p.name}
              </h3>
              {p.grade && (
                <div className="mt-1 font-mono text-[0.62rem] uppercase tracking-widest2 text-terracotta">
                  {p.grade}
                </div>
              )}
              {p.desc && (
                <p className="mt-3 text-sm text-olive-soft leading-relaxed">
                  {p.desc}
                </p>
              )}
              <div className="mt-5 pt-5 border-t border-olive/10">
                <div className="font-mono text-[0.6rem] uppercase tracking-widest2 text-olive-soft">
                  {useLabel}
                </div>
                <div className="mt-1 text-sm text-olive-deep">{p.use}</div>
              </div>
              <div className="mt-5 text-[0.6rem] font-mono uppercase tracking-widest2 text-terracotta">
                {badge}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function DairyCatalog() {
  return (
    <>
      <section
        className="max-w-8xl mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-4"
        data-testid="dairy-intro"
      >
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="overline">Pillar 01 · Dairy Byproducts</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6">
              The full dairy portfolio.
            </h2>
          </div>
          <span className="badge-pill" data-testid="export-ready-badge">
            Export Ready · APEDA Aligned
          </span>
        </div>
      </section>
      <CatalogGrid
        items={PRODUCTS}
        badge="100% In-House · Organic · Traceable"
        testidPrefix="catalog-product"
      />
    </>
  );
}

function PharmaCatalog() {
  return (
    <>
      <section
        className="max-w-8xl mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-4"
        data-testid="pharma-intro"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <span className="overline">Pillar 02 · Pharma & Nutraceutical Crops</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6 max-w-2xl">
              17 herbs. One owned soil.
            </h2>
            <p className="mt-6 text-olive-soft leading-relaxed max-w-2xl">
              Pharmaceutical-grade Moringa, Ashwagandha, Curcumin and 15+
              Ayurvedic crops cultivated on our owned acreage — supplied to
              pharma, nutraceutical and Ayurvedic manufacturers worldwide.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <span className="badge-pill" data-testid="pharma-grade-badge">
              Chemical-Free · Lab Tested · Export Grade
            </span>
          </div>
        </div>
      </section>
      <CatalogGrid
        items={PHARMA_CROPS}
        badge="100% Organic · Export Grade · Pharma Ready"
        testidPrefix="catalog-pharma"
        useLabel="Pharma · Nutraceutical · Export"
      />
    </>
  );
}

function CattleCatalog() {
  return (
    <>
      <section
        className="max-w-8xl mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-4"
        data-testid="cattle-intro"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <span className="overline">Pillar 03 · Premium Cattle Breeding</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6 max-w-2xl">
              Pedigree breeds.{" "}
              <span className="italic text-terracotta">Verified lots.</span>
            </h2>
            <p className="mt-6 text-olive-soft leading-relaxed max-w-2xl">
              From our owned breeding program — sold to dairy farms,
              cooperatives, agri-institutions and government livestock
              programs. Every animal ships with full lineage and health
              documentation.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              className="btn-outline"
              data-testid="cattle-register-cta"
            >
              Register Interest
              <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
      <CatalogGrid
        items={CATTLE_BREEDS}
        badge="Owned Genetics · Verified Lineage · Health-Certified"
        testidPrefix="catalog-cattle"
        useLabel="Buyer Profile"
      />
    </>
  );
}
